
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface OrderDetails {
  order_id: string;
  user_email: string;
  amount: number;
  status: string;
  created_at: string;
  product_name: string;
  payment_method?: string;
  currency?: string;
  user_name?: string;
}

interface OrdersTableProps {
  orders: OrderDetails[];
}

const OrdersTable = ({ orders }: OrdersTableProps) => {
  // Helper function to standardize status display
  const getStatusDisplay = (status: string) => {
    if (!status) return 'PENDING';
    
    const normalizedStatus = status.toUpperCase();
    
    // If it's still the template placeholder, return PENDING
    if (normalizedStatus === '{LINK_STATUS}') {
      return 'PENDING';
    }
    
    // Group success statuses
    if (['PAID', 'SUCCESS', 'PAYMENT_SUCCESS', 'PAYMENT_LINK_PAID', 'ACTIVE'].includes(normalizedStatus)) {
      return 'PAID';
    }
    
    // Group pending statuses
    if (['PENDING', 'INITIATED', 'PAYMENT_INITIATED', 'PAYMENT_LINK_CREATED'].includes(normalizedStatus)) {
      return 'PENDING';
    }
    
    // Group failed statuses
    if (['FAILED', 'PAYMENT_FAILED', 'EXPIRED', 'CANCELLED', 'PAYMENT_LINK_EXPIRED', 'PAYMENT_LINK_CANCELLED'].includes(normalizedStatus)) {
      return 'FAILED';
    }
    
    // Default fallback
    return status || 'Unknown';
  };
  
  // Helper function to get appropriate status styling
  const getStatusStyling = (status: string) => {
    const displayStatus = getStatusDisplay(status);
    
    if (displayStatus === 'PAID') {
      return 'bg-green-100 text-green-800';
    } else if (displayStatus === 'PENDING') {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order ID</TableHead>
            <TableHead>Email</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Product</TableHead>
            <TableHead className="text-center">Status</TableHead>
            <TableHead>Payment Method</TableHead>
            <TableHead className="text-right">Created At</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!orders || orders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-4">
                <div className="space-y-2">
                  <p>No orders found.</p>
                  <p className="text-sm text-gray-500">
                    This may be due to Row Level Security (RLS) policies in Supabase or no orders exist yet.
                  </p>
                  <p className="text-sm text-gray-500">
                    Please make sure you're logged in as an admin and have the proper permissions.
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order) => (
              <TableRow key={order.order_id} className="hover:bg-muted/20">
                <TableCell className="font-mono text-xs">
                  {order.order_id}
                </TableCell>
                <TableCell>
                  {order.user_email || 'N/A'}
                </TableCell>
                <TableCell className="text-right">
                  {order.currency || '₹'}{order.amount || 0}
                </TableCell>
                <TableCell>
                  {order.product_name || 'N/A'}
                </TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusStyling(order.status)}`}>
                    {getStatusDisplay(order.status)}
                  </span>
                </TableCell>
                <TableCell>
                  {order.payment_method || 'N/A'}
                </TableCell>
                <TableCell className="text-right text-xs">
                  {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default OrdersTable;
